import { colors } from '../../colors';
import type { SelectSize, SelectStyleProps } from './types';

export const getSelectSizeStyles = (size: SelectSize): string => {
  switch (size) {
    case 'small':
      return `
        padding: 8px 44px 8px 12px;
        font-size: 14px;
        line-height: 20px;
        min-height: 32px;
      `;
    case 'large':
      return `
        padding: 12px 48px 12px 16px;
        font-size: 18px;
        line-height: 28px;
        min-height: 48px;
      `;
    case 'medium':
    default:
      return `
        padding: 10px 44px 10px 14px;
        font-size: 16px;
        line-height: 24px;
        min-height: 40px;
      `;
  }
};

export const getSelectFieldStyles = (styleProps?: SelectStyleProps, isOpen?: boolean): string => {
  const {
    backgroundColor,
    borderColor,
    focusBorderColor,
    color,
    fontSize,
    borderRadius,
    padding,
    width,
  } = styleProps ?? {};

  return `
    background-color: ${backgroundColor ?? colors.white};
    border: 1px solid ${
      isOpen ? (focusBorderColor ?? colors.primary[300]) : (borderColor ?? colors.gray[300])
    };
    border-radius: ${borderRadius ?? '6px'};
    color: ${color ?? colors.gray[800]};
    font-weight: 400;
    ${fontSize ? `font-size: ${fontSize};` : ''}
    ${padding ? `padding: ${padding};` : ''}
    ${width ? `width: ${width};` : ''}
    transition: all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1);

    &:hover:not(:disabled) {
      border-color: ${(() => {
        if (isOpen) return focusBorderColor ?? colors.primary[300];
        return borderColor ? `${borderColor}CC` : colors.gray[400];
      })()};
    }

    &:focus-within {
      border-color: ${focusBorderColor ?? colors.primary[300]};
      outline: none;
    }

    &:disabled {
      background-color: ${colors.gray[100]};
      color: ${colors.gray[500]};
      border-color: ${colors.gray[200]};
      cursor: not-allowed;
      opacity: 0.6;
    }
  `;
};

export const getSelectPanelStyles = (styleProps?: SelectStyleProps): string => {
  const { backgroundColor, maxHeight } = styleProps ?? {};

  return `
    background-color: ${backgroundColor ?? colors.white};
    max-height: ${maxHeight ?? '300px'};
    overflow: hidden;
  `;
};

export const getSelectDropdownStyles = (
  styleProps?: SelectStyleProps,
  size?: SelectSize
): string => {
  const { maxHeight } = styleProps ?? {};

  const getPadding = () => {
    switch (size) {
      case 'small':
        return '0 0 4px 0';
      case 'large':
        return '0 0 12px 0';
      case 'medium':
      default:
        return '0 0 8px 0';
    }
  };

  return `
    max-height: ${maxHeight ?? '240px'};
    overflow-y: auto;
    padding: ${getPadding()};
  `;
};

export const getSelectOptionStyles = (
  active: boolean,
  selected: boolean,
  disabled: boolean,
  styleProps?: SelectStyleProps,
  size?: SelectSize
): string => {
  const { optionHoverBackgroundColor, selectedOptionBackgroundColor, color } = styleProps ?? {};

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return `
          padding: 8px 12px;
          font-size: 14px;
          line-height: 20px;
        `;
      case 'large':
        return `
          padding: 16px 20px;
          font-size: 16px;
          line-height: 24px;
        `;
      case 'medium':
      default:
        return `
          padding: 12px 16px;
          font-size: 14px;
          line-height: 20px;
        `;
    }
  };

  let styles = `
    ${getSizeStyles()}
    cursor: pointer;
    color: ${color ?? colors.gray[700]};
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 400;
    transition: background-color 0.1s ease-in-out;
    border-radius: 0;
    margin: 0;
    position: relative;
  `;

  if (disabled) {
    styles += `
      opacity: 0.5;
      cursor: not-allowed;
      color: ${colors.gray[400]};
    `;
  } else {
    if (active && !selected) {
      styles += `
        background-color: ${optionHoverBackgroundColor ?? colors.primary[50]};
        color: ${colors.primary[300]};
      `;
    }
    if (selected) {
      styles += `
        background-color: ${selectedOptionBackgroundColor ?? colors.primary[50]};
        color: ${colors.primary[300]};
        font-weight: 500;
      `;
    }
    if (!active && !selected) {
      styles += `
        &:hover {
          background-color: ${optionHoverBackgroundColor ?? colors.primary[50]};
          color: ${colors.primary[300]};
        }
      `;
    }
  }

  return styles;
};

export const getSelectSearchInputStyles = (
  styleProps?: SelectStyleProps,
  size?: SelectSize
): string => {
  const { color } = styleProps ?? {};

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return `
          height: 28px;
          padding: 0 8px;
          font-size: 14px;
        `;
      case 'large':
        return `
          height: 40px;
          padding: 0 12px;
          font-size: 16px;
        `;
      case 'medium':
      default:
        return `
          height: 32px;
          padding: 0 8px;
          font-size: 14px;
        `;
    }
  };

  return `
    width: 100%;
    ${getSizeStyles()}
    border: none;
    border-radius: 0;
    outline: none;
    color: ${color ?? colors.gray[600]};
    background: transparent;
    transition: none;
    font-weight: 400;

    &::placeholder {
      color: ${colors.gray[400]};
      font-weight: 400;
    }

    &:focus {
      outline: none;
      border: none;
      box-shadow: none;
    }

    &:hover {
      outline: none;
      border: none;
    }
  `;
};

export const getSelectSearchContainerStyles = (size?: SelectSize): string => {

  const getPadding = () => {
    switch (size) {
      case 'small':
        return '8px';
      case 'large':
        return '16px';
      case 'medium':
      default:
        return '12px';
    }
  };

  return `
    padding: ${getPadding()};
    border-bottom: 1px solid ${colors.gray[200]};
    background: ${colors.white};
  `;
};

export const getSelectTagStyles = (styleProps?: SelectStyleProps): string => {
  const { tagBackgroundColor, tagColor, borderRadius } = styleProps ?? {};

  return `
    background-color: ${tagBackgroundColor ?? colors.primary[100]};
    color: ${tagColor ?? colors.primary[300]};
    border: 1px solid ${colors.primary[300]}30;
    border-radius: ${borderRadius ?? '6px'};
    padding: 4px 8px;
    font-size: 14px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 2px 2px 2px 0;
    transition: all 0.15s ease-in-out;
    max-width: calc(100% - 64px);
    min-width: 0;
    flex-shrink: 0;

    &:hover {
      background-color: ${colors.primary[300]}20;
      border-color: ${colors.primary[300]}50;
    }
  `;
};